/**
 * Núcleo NÃO-VISUAL do coletor (M4): normalização do bipe, leitura do envelope
 * de `POST /warehouse/scan` e as duas formas de dizer um endereço.
 *
 * ── Por que existe ──────────────────────────────────────────────────────────
 * O coletor tem uma regra de ouro que vem do PRD (§8.2 / §4): a validação nos
 * fluxos guiados é IGUALDADE EXATA com o código esperado — sem dígito
 * verificador, sem casamento parcial, e o padding faz parte do protocolo
 * (`A5` ≠ `A05`). Toda comparação de bipe do módulo passa por `CodesMatch`
 * daqui; nenhuma tela compara string na mão.
 *
 * O PARSING de código (dizer se `A-01` é bloco ou posição) NÃO mora aqui —
 * §2.2 diz que o classificador é do backend, um lugar só, alimentado pelos
 * `scan_pattern.*` de `wms_settings`. O front só normaliza (caixa/espaço) e
 * LÊ a resposta. Os padrões abaixo são usados apenas para MONTAR códigos
 * derivados (o STG da rua) e para reconhecer a forma de um código já
 * classificado — nunca para decidir o tipo de um bipe.
 */

/** Grade de códigos (§4). Um dígito no andar: `A5`, nunca `A05`. */
export const RACK_CODE_PATTERN = /^[A-Z]{1,2}-\d{2}-A\d-P[12]$/;
export const BLOCK_CODE_PATTERN = /^[A-Z]{1,2}-\d{2}$/;
export const STREET_STAGING_CODE_PATTERN = /^STG-R\d{2}-00-00$/;

/** Tipos que o `POST /warehouse/scan` devolve. */
export const SCAN_TYPE_LOCATION = 'LOCATION';
export const SCAN_TYPE_BLOCK = 'BLOCK';
export const SCAN_TYPE_LPN = 'LPN';
export const SCAN_TYPE_UNKNOWN = 'UNKNOWN';

/** Como a leitura entrou no sistema — vai no wire de todo movimento. */
export const SCAN_METHOD_SCANNED = 'SCANNED';
export const SCAN_METHOD_TYPED = 'TYPED';

/** Kinds de incidente que o COLETOR abre (o enum do backend é maior). */
export const INCIDENT_KIND_SCAN_MISMATCH = 'SCAN_MISMATCH';
export const INCIDENT_KIND_UNREGISTERED_OCCUPANCY = 'UNREGISTERED_OCCUPANCY';
export const INCIDENT_KIND_MISSING_PALLET = 'MISSING_PALLET';

const toFinite = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};


/**
 * Forma canônica de um bipe: sem espaço nas pontas, sem espaço interno (o
 * leitor às vezes injeta um) e em CAIXA ALTA — os códigos do §4 são todos
 * maiúsculos. É a ÚNICA transformação permitida antes de comparar: qualquer
 * coisa além disso já seria "casamento parcial", que o PRD proíbe.
 */
export function NormalizeScanCode(raw) {
    return String(raw ?? '')
        .replace(/\s+/g, '')
        .trim()
        .toUpperCase();
}

/** Igualdade EXATA entre o esperado e o lido (ambos normalizados). */
export function CodesMatch(expected, read) {
    const left = NormalizeScanCode(expected);
    const right = NormalizeScanCode(read);
    return left.length > 0 && left === right;
}

/** `A-01` — a ÚNICA etiqueta física do rack (§4: andar/posição não têm etiqueta). */
export function BuildBlockCode(rack, bay) {
    return `${String(rack || '').toUpperCase()}-${String(Number(bay) || 0).padStart(2, '0')}`;
}


/** `STG-R02-00-00` — cabeceira da rua, destino do desvio do §6.2. */
/** Cabeceira (staging) do RACK: `STG-A-00` — uma por porta-pallet. */
export function BuildRackStagingCode(rack) {
    return `STG-${String(rack || '').toUpperCase()}-00`;
}

export function IsRackLocation(location) {
    return location?.type === 'RACK' || RACK_CODE_PATTERN.test(NormalizeScanCode(location?.code));
}

/**
 * O código que o operador consegue BIPAR para provar presença nesta posição.
 *
 * No rack é o código do BLOCO: andar e posição são apresentação de tela, não
 * existe etiqueta para eles (§4). Fora do rack (staging, doca, fábrica,
 * incidente) a etiqueta é o código literal do endereço.
 */
export function ExpectedScanCodeFor(location) {
    if (!location) {
        return null;
    }
    const code = NormalizeScanCode(location.code);
    if (IsRackLocation(location)) {
        const rack = location.rack ? String(location.rack).toUpperCase() : null;
        const bay = toFinite(location.bay ?? location.block);
        if (rack && bay !== null) {
            return `${rack}-${String(bay).padStart(2, '0')}`;
        }
        // Sem coordenadas na linha: o prefixo do próprio código de posição É o
        // código do VÃO (`A-01-A2-P1` → `A-01`).
        const partes = code.match(/^([A-Z]{1,2}-\d{2})-A\d+-P\d+$/);
        return partes ? partes[1] : null;
    }
    return code || null;
}

/**
 * Endereço em TEXTO CLARO — a linha que o operador lê de cima da empilhadeira.
 *
 * Duas formas, como no protótipo do dono: a curta é navegação ("para onde eu
 * dirijo") e a longa é mira ("onde exatamente eu encosto o garfo"). Números
 * NÃO levam padding aqui — padding é coisa de código, não de fala: quem lê diz
 * "Rack A, Bloco 10", e `A-10` fica na linha monoespaçada embaixo.
 *
 * Não repetimos o defeito do protótipo de chamar a posição de "Direita/Ímpar":
 * paridade é atributo do BLOCO e P1/P2 é atributo da POSIÇÃO — misturar os dois
 * numa palavra só foi apontado como não-conformidade. Aqui a posição é dita
 * como "Posição 1"; qual lado ela é fica por conta da torre do bloco.
 */
export function FormatLocationPlain(location, { full = true, label = null } = {}) {
    if (!location) {
        return '';
    }
    // Gramática do porta-pallet (005): rack é LETRA, bloco é sequencial. "Rua 0"
    // era este formatador tentando ler letra como número — o dono barrou.
    const rack = location.rack ? String(location.rack).toUpperCase() : null;
    const bay = toFinite(location.bay ?? location.block);
    const level = toFinite(location.level);
    const position = toFinite(location.position);
    const word = (key, fallback) => (label ? label(key) : fallback);

    if (rack && bay !== null) {
        const head = `${word('wms.field.rack', 'Rack')} ${rack}, ${word('wms.field.bay', 'Bloco')} ${bay}`;
        if (!full || level === null) {
            return head;
        }
        const tail = position === null ? '' : `, ${word('wms.field.position', 'Posição')} ${position}`;
        return `${head}, ${word('wms.field.level', 'Andar')} ${level}${tail}`;
    }

    // Cabeceira do porta-pallet: o endereço é o RACK, e o vão não existe.
    if (rack) {
        const typeName = word(`wms.location_type.${location.type}`, 'Staging');
        return `${typeName} — ${word('wms.field.rack', 'Rack')} ${rack}`;
    }

    // Endereços fixos (staging, doca, fábrica, incidente): fala-se o NOME do
    // lugar — nunca "Rua 0, Bloco 0". O código fica na linha monoespaçada.
    if (String(location.code || '').toUpperCase() === 'STG-PACKING') {
        return 'Staging do Packing (bancada de envios)';
    }
    return location.type ? word(`wms.location_type.${location.type}`, location.code) : location.code || '';
}


/** Texto de UMA linha juntando fala e código — usado em resumo e listas. */
export function FormatLocationSummary(location, options = {}) {
    if (!location) {
        return '';
    }
    const plain = FormatLocationPlain(location, options);
    return plain && plain !== location.code ? `${plain} · ${location.code}` : location.code || plain;
}

function firstArray(...candidates) {
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }
    return [];
}

function firstObject(...candidates) {
    for (const candidate of candidates) {
        if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
            return candidate;
        }
    }
    return null;
}

/**
 * Normaliza a resposta do `POST /warehouse/scan` numa forma ÚNICA para as
 * telas. O contrato é `{type, location?, lpn?, block?}`, mas cada tipo carrega
 * um conteúdo diferente (LPN traz itens + onde está; posição traz os LPNs que
 * estão nela; bloco traz as posições e a ocupação), e o backend do M4 sobe em
 * paralelo a esta tela — ler o envelope num lugar só é o que impede que uma
 * chave com nome levemente diferente vire seis correções espalhadas.
 *
 * Devolve sempre: {type, location, lpns, block:{street,block,code}, positions, lpn, items}.
 */
export function ParseScanResult(payload) {
    const root = firstObject(payload) || {};
    const type = String(root.type || SCAN_TYPE_UNKNOWN).toUpperCase();

    const location = firstObject(root.location);
    const lpn = firstObject(root.lpn);
    const blockRaw = firstObject(root.bay) || firstObject(root.block);

    const positions = firstArray(blockRaw?.positions, blockRaw?.locations, root.positions, root.locations);
    const lpns = firstArray(location?.lpns, root.lpns);
    const items = firstArray(lpn?.items, root.items);

    const block = blockRaw
        ? {
              rack: blockRaw.rack ?? null,
              bay: toFinite(blockRaw.bay ?? blockRaw.block),
              code: blockRaw.code || BuildBlockCode(blockRaw.rack, blockRaw.bay ?? blockRaw.block),
              bay_width: blockRaw.bay_width ?? null,
          }
        : null;

    return { type, location, lpns, block, positions, lpn, items };
}

/** Uma posição está livre? (o backend manda `occupancy`; a falta dela é FREE) */
export function IsPositionFree(row) {
    const occupancy = String(row?.occupancy || 'FREE').toUpperCase();
    const status = String(row?.status || 'ACTIVE').toUpperCase();
    return occupancy === 'FREE' && status === 'ACTIVE';
}

/**
 * Uma posição está OCUPADA? (tem pallet em cima)
 *
 * NÃO é o contrário de `IsPositionFree`: "livre" exige vazia **e** ACTIVE, então
 * negar essa função joga pallet e manutenção no mesmo balde — e um vão vazio em
 * manutenção não está ocupado, está indisponível. Contar pallet com `!IsPositionFree`
 * inflava o "N Ocupada" com toda posição bloqueada do bloco.
 *
 * Quem OFERECE destino pergunta `IsPositionFree` (manutenção não serve de destino);
 * quem CONTA pallet pergunta esta. RESERVED (em trânsito) não entra: ainda não há
 * pallet no vão.
 */
export function IsPositionOccupied(row) {
    return String(row?.occupancy || 'FREE').toUpperCase() === 'OCCUPIED';
}

/**
 * Motivos de bloqueio do 409 da engine (`error.details.blocks`). Devolve
 * sempre um array de `{rule, message}` — mesmo quando o backend manda strings
 * soltas ou nada (aí a mensagem do próprio erro vira o único motivo).
 */
export function ParseEngineBlocks(error) {
    const raw = error?.details?.blocks ?? error?.details?.error?.details?.blocks;
    if (Array.isArray(raw) && raw.length > 0) {
        return raw.map((item, index) =>
            typeof item === 'string'
                ? { rule: `BLOCK_${index + 1}`, message: item }
                : { rule: item?.rule || `BLOCK_${index + 1}`, message: item?.message || String(item?.rule || '') },
        );
    }
    return error?.message ? [{ rule: 'BLOCKED', message: error.message }] : [];
}
